/**
 * Sources — look up any cited source and tell BLIP it got the category wrong.
 *
 * Two corrections, both GLOBAL (every report that cites the source updates):
 *   - the Source Category of a whole domain, or of one `site/path` section;
 *   - the Supplier Content Type of one supplier page (Joan's 22-Sept taxonomy).
 *
 * The rule is saved at once; a background job then re-derives and re-aggregates
 * every stored report that cites the source, and the "Recent corrections" list
 * shows its progress. Every correction can be undone exactly.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  PAGE_TYPES,
  SOURCE_CATEGORIES,
  type SourceCategory,
  type SourceCorrection,
  type SourceDecidedBy,
  type SourceLookupResult,
  type SupplierPageType,
} from '@aurora/shared';
import { api, ApiClientError } from '../api/client';

/** 'No sources' describes an answer, never a cited site. */
const CATEGORY_CHOICES = SOURCE_CATEGORIES.filter((c) => c !== 'No sources');

function errText(e: unknown, fallback: string): string {
  return e instanceof ApiClientError ? e.message : fallback;
}

function whyText(d: SourceDecidedBy): string {
  switch (d.kind) {
    case 'supplier':
      return `It is a website of “${d.supplier}” in the supplier dictionary, which decides its category. Change it on the Suppliers page.`;
    case 'rule':
      if (d.origin === 'built-in')
        return `BLIP’s built-in rule for ${d.pattern}.`;
      if (d.origin.startsWith('manual'))
        return `A correction for ${d.pattern} (${d.origin.replace(/^manual:\s*/, 'by ')}).`;
      return `A learned rule for ${d.pattern} (${d.origin}).`;
    case 'tld':
      return 'No rule matches, so the .edu / .gov fallback applies.';
    default:
      return 'No rule matches this site yet, so its citations read as Unclassified.';
  }
}

function progressText(c: SourceCorrection): string {
  switch (c.status) {
    case 'queued':
      return 'Waiting to update reports…';
    case 'applying':
    case 'undoing':
      return `Updating ${c.runsDone} of ${c.runsTotal} report${c.runsTotal === 1 ? '' : 's'}…`;
    case 'done':
      return `Done: ${c.runsTotal} report${c.runsTotal === 1 ? '' : 's'} updated`;
    case 'undone':
      return `Undone: ${c.runsTotal} report${c.runsTotal === 1 ? '' : 's'} restored`;
    default:
      return `Failed: ${c.error ?? 'unknown error'}`;
  }
}

function statusPill(c: SourceCorrection): string {
  if (c.status === 'done') return 'pill pill-complete';
  if (c.status === 'failed') return 'pill pill-failed';
  if (c.status === 'undone') return 'pill pill-draft';
  return 'pill pill-pending';
}

export function Sources(): JSX.Element {
  const [q, setQ] = useState('');
  const [result, setResult] = useState<SourceLookupResult | null>(null);
  const [looking, setLooking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [category, setCategory] = useState('');
  const [scope, setScope] = useState<'domain' | 'path'>('domain');
  const [corrections, setCorrections] = useState<SourceCorrection[]>([]);

  const loadCorrections = useCallback(async (): Promise<void> => {
    try {
      const r = await api.listSourceCorrections();
      setCorrections(r.corrections);
    } catch {
      /* non-fatal — the list just stays as it was */
    }
  }, []);

  useEffect(() => {
    void loadCorrections();
  }, [loadCorrections]);

  // Poll while any correction is still updating reports.
  const working = corrections.some((c) =>
    ['queued', 'applying', 'undoing'].includes(c.status),
  );
  useEffect(() => {
    if (!working) return undefined;
    const t = setInterval(() => void loadCorrections(), 3000);
    return () => clearInterval(t);
  }, [working, loadCorrections]);

  const lookup = async (query = q): Promise<void> => {
    if (!query.trim()) return;
    setLooking(true);
    setError(null);
    setNotice(null);
    try {
      const r = await api.lookupSource(query.trim());
      setResult(r);
      setCategory(r.category ?? '');
      setScope('domain');
    } catch (e) {
      setResult(null);
      setError(errText(e, 'Lookup failed'));
    } finally {
      setLooking(false);
    }
  };

  const saveCategory = async (): Promise<void> => {
    if (!result || !category) return;
    const path = scope === 'path' && result.pathScope;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await api.correctSourceCategory({
        pattern: path ? result.pathScope! : result.host,
        matchType: path ? 'substring' : 'domain',
        category: category as SourceCategory,
      });
      setNotice(
        `Saved. ${path ? result.pathScope : result.host} is now ${category}; every report that cites it is being updated.`,
      );
      await loadCorrections();
      await lookup(path ? (result.pathScope ?? result.host) : result.host);
    } catch (e) {
      setError(errText(e, 'Could not save the correction'));
    } finally {
      setBusy(false);
    }
  };

  const saveContentType = async (
    url: string,
    contentType: SupplierPageType,
  ): Promise<void> => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await api.correctContentType({ url, contentType });
      setNotice(`Saved. That page is now ${contentType}.`);
      await loadCorrections();
      if (result) await lookup(result.pathScope ?? result.host);
    } catch (e) {
      setError(errText(e, 'Could not save the page type'));
    } finally {
      setBusy(false);
    }
  };

  const undo = async (c: SourceCorrection): Promise<void> => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await api.undoSourceCorrection(c.id);
      setNotice(`Undoing the change to ${c.target}.`);
      await loadCorrections();
    } catch (e) {
      setError(errText(e, 'Could not undo'));
    } finally {
      setBusy(false);
    }
  };

  // Undo is exact only for the newest live correction on each source (the
  // server enforces this too), so offer it there alone.
  const undoable = new Set<string>();
  const seenTargets = new Set<string>();
  for (const c of corrections) {
    if (c.status === 'undone' || c.status === 'undoing') continue;
    const key = `${c.kind}|${c.target}|${c.matchType ?? ''}`;
    if (seenTargets.has(key)) continue;
    seenTargets.add(key);
    if (c.status === 'done' || c.status === 'failed') undoable.add(c.id);
  }

  const supplierDecided = result?.decidedBy.kind === 'supplier';
  const supplierPages = result?.pages.filter((p) => p.contentType) ?? [];
  const unchanged =
    !result ||
    !category ||
    (scope === 'domain' && category === (result.category ?? ''));

  return (
    <div className="benchmarks">
      <div className="page-head">
        <h1>Sources</h1>
      </div>
      <p className="muted">
        Look up any website the AI models cite and correct how BLIP categorizes
        it. A correction applies to every report that cites the source, and you
        can undo it. Report summaries written before a change keep their
        wording until the report is re-interpreted.
      </p>

      <form
        className="filters"
        onSubmit={(e) => {
          e.preventDefault();
          void lookup();
        }}
      >
        <input
          type="search"
          placeholder="Domain or URL, e.g. labroots.com or https://www.nature.com/articles/…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{ flex: 1 }}
        />
        <button
          type="submit"
          className="btn btn-primary"
          disabled={looking || !q.trim()}
        >
          {looking ? 'Looking up…' : 'Look up'}
        </button>
      </form>

      {error && <div className="form-error">{error}</div>}
      {notice && <div className="form-notice">{notice}</div>}

      {result && (
        <div className="card">
          <h2 className="section-head">
            <span>{result.host}</span>
          </h2>
          <p>
            Category: <strong>{result.category ?? 'Unclassified'}</strong>
            <br />
            <span className="muted">{whyText(result.decidedBy)}</span>
            <br />
            <span className="muted">
              Cited in {result.answers.toLocaleString()} answer
              {result.answers === 1 ? '' : 's'} across{' '}
              {result.reports.toLocaleString()} report
              {result.reports === 1 ? '' : 's'}.
            </span>
          </p>

          {!supplierDecided && (
            <div className="filters">
              <label>
                Change to{' '}
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  disabled={busy}
                >
                  <option value="">Choose a category…</option>
                  {CATEGORY_CHOICES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </label>
              {result.pathScope && (
                <label>
                  for{' '}
                  <select
                    value={scope}
                    onChange={(e) =>
                      setScope(e.target.value as 'domain' | 'path')
                    }
                    disabled={busy}
                  >
                    <option value="domain">the whole site ({result.host})</option>
                    <option value="path">
                      only this section ({result.pathScope})
                    </option>
                  </select>
                </label>
              )}
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => void saveCategory()}
                disabled={busy || unchanged}
              >
                {busy ? 'Saving…' : 'Save'}
              </button>
            </div>
          )}

          {supplierPages.length > 0 && (
            <>
              <h3>Supplier pages cited on this site</h3>
              <p className="muted">
                Each page’s Supplier Content Type. “AI” means the classifier
                chose it; a type you set is never changed by the AI.
              </p>
              <table className="leads-table">
                <thead>
                  <tr>
                    <th>Page</th>
                    <th>Citations</th>
                    <th>Type</th>
                    <th>Set by</th>
                  </tr>
                </thead>
                <tbody>
                  {supplierPages.map((p) => (
                    <tr key={p.urlKey}>
                      <td style={{ wordBreak: 'break-all' }}>
                        <a href={p.url} target="_blank" rel="noreferrer">
                          {p.url.replace(/^https?:\/\/(www\.)?/, '')}
                        </a>
                      </td>
                      <td>{p.citations}</td>
                      <td>
                        <select
                          value={p.contentType ?? 'Unclassified'}
                          onChange={(e) =>
                            void saveContentType(
                              p.url,
                              e.target.value as SupplierPageType,
                            )
                          }
                          disabled={busy}
                        >
                          {PAGE_TYPES.map((t) => (
                            <option key={t} value={t}>
                              {t}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="muted">
                        {p.contentTypeBy === 'manual'
                          ? 'You'
                          : p.contentTypeBy === 'ai'
                            ? 'AI'
                            : 'Not yet typed'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {result.pages.length > 0 && supplierPages.length === 0 && (
            <>
              <h3>Most-cited pages</h3>
              <table className="leads-table">
                <thead>
                  <tr>
                    <th>Page</th>
                    <th>Citations</th>
                    <th>Category</th>
                  </tr>
                </thead>
                <tbody>
                  {result.pages.slice(0, 15).map((p) => (
                    <tr key={p.urlKey}>
                      <td style={{ wordBreak: 'break-all' }}>
                        <a href={p.url} target="_blank" rel="noreferrer">
                          {p.url.replace(/^https?:\/\/(www\.)?/, '')}
                        </a>
                      </td>
                      <td>{p.citations}</td>
                      <td>{p.category ?? 'Unclassified'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}

      <h2 className="section-head">
        <span>Recent corrections</span>
      </h2>
      {corrections.length === 0 ? (
        <div className="empty-state muted">No corrections yet.</div>
      ) : (
        <table className="leads-table">
          <thead>
            <tr>
              <th>Source</th>
              <th>Change</th>
              <th>By</th>
              <th>When</th>
              <th>Reports</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {corrections.map((c) => (
              <tr key={c.id}>
                <td style={{ wordBreak: 'break-all' }}>
                  {c.target.replace(/^https?:\/\/(www\.)?/, '')}
                  <div className="muted">
                    {c.kind === 'category' ? 'Source category' : 'Page type'}
                  </div>
                </td>
                <td>
                  {c.oldValue ?? 'Unclassified'} → <strong>{c.newValue}</strong>
                </td>
                <td className="muted">{c.createdBy ?? '—'}</td>
                <td className="muted">
                  {new Date(c.createdAt).toLocaleString()}
                </td>
                <td>
                  <span className={statusPill(c)}>{c.status}</span>{' '}
                  <span className="muted">{progressText(c)}</span>
                </td>
                <td>
                  {undoable.has(c.id) && (
                    <button
                      type="button"
                      className="btn btn-ghost"
                      onClick={() => void undo(c)}
                      disabled={busy}
                    >
                      Undo
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
